import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { basicSetup, EditorView } from "codemirror";
import { EditorState } from "@codemirror/state";
import { python } from "@codemirror/lang-python";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAnglesLeft,
  faAnglesRight,
  faCircleCheck,
  faFileCirclePlus,
  faFloppyDisk,
  faFolderPlus,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { postPlaygroundProject, putPlaygroundProject, postStudentLessonSubmission, postStudentLessonWorkspaceReset, type DemoMarkDoneResponse, type PlaygroundProjectFile } from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import {
  SIGN_IN_TO_CHECK_MESSAGE,
  SIGN_IN_TO_SAVE_MESSAGE,
  userFacingRequestError,
} from "../auth/session-keepalive";
import { usePythonRuntime } from "../python-runtime/usePythonRuntime";
import { IdeOutputPanel } from "../python-runtime/IdeOutputPanel";
import { pythonEditorExtensions } from "../student-ide/pythonEditorConfig";
import { celebratePassing } from "../student-ide/confettiCelebration";
import {
  buildMarkDoneFromLessonTestBlocks,
  buildServerTestResults,
  submissionFlagsFromBlocks,
  mainPyFromProjectFiles,
  type LessonRuntimeMarkDoneSpec,
} from "../student-ide/lessonMarkDoneHelpers";
import type { DemoTourStep } from "../student-ide/demoTourSteps";
import { StudentIdeTourPanel } from "../student-ide/StudentIdeTourPanel";
import type { LessonImmersiveTheme } from "../teacher/lessonImmersiveTheme";
import type { AssignmentProgressStatus } from "../student/assignmentProgressStatus";
import { ProgressStatusChip } from "../student/ProgressStatusChip";
import { LAB_PROGRESS_HINTS } from "../student/assignmentProgressStatus";
import { PlaygroundFileExplorer, type PendingNewItem } from "./PlaygroundFileExplorer";
import { PlaygroundPanelSplitter } from "./PlaygroundPanelSplitter";
import { usePlaygroundPanelSizes } from "./usePlaygroundPanelSizes";
import {
  collectFolderPaths,
  deletePlaygroundFolderPaths,
  inferPlaygroundFilePath,
  isHiddenPlaygroundFile,
  joinPlaygroundPath,
  normalizePlaygroundProjectFiles,
  parentFolderPath,
  renamePlaygroundFolderPaths,
  visiblePlaygroundFiles,
} from "./playground-file-tree";
import { starterFilesToProjectFiles } from "../teacher/course-template-helpers";
import {
  readUploadAsText,
  validatePlaygroundFilePath,
  validatePlaygroundFileSet,
  validatePlaygroundFolderPath,
} from "./playground-files";
import {
  clearPlaygroundShadow,
  filesPreferringShadow,
  playgroundFilesEqual,
  readPlaygroundShadow,
  writePlaygroundShadow,
} from "./playground-shadow";

const ideEditorTheme = EditorView.theme(
  {
    "&": {
      height: "100%",
      width: "100%",
      maxWidth: "100%",
      backgroundColor: "var(--ide-editor-bg)",
      color: "var(--ide-editor-fg)",
    },
    ".cm-scroller": {
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
      fontSize: "0.9rem",
      lineHeight: 1.45,
    },
    ".cm-content": { caretColor: "var(--color-forest)" },
    ".cm-gutters": {
      backgroundColor: "rgba(27, 77, 46, 0.07)",
      color: "var(--color-brown)",
      borderRight: "1px solid rgba(27, 77, 46, 0.12)",
    },
    ".cm-activeLineGutter": {
      backgroundColor: "rgba(107, 163, 54, 0.12)",
    },
  },
  { dark: false },
);

type SaveStatus = "saved" | "unsaved" | "saving" | "error";
type QuickScriptMode = "simple" | "advanced";
type QuickMobileTab = "code" | "files" | "console";

const AUTOSAVE_DEBOUNCE_MS = 800;

export interface PlaygroundBodyProps {
  readonly eyebrow: string;
  readonly title: string;
  readonly backTo: string;
  readonly backLabel: string;
  readonly initialFiles: readonly PlaygroundProjectFile[];
  /** Owner mode: persists edits and post-run file writes to this project. */
  readonly projectId?: string;
  /** When set (and no projectId), persist file state to localStorage under this key. */
  readonly filesStorageKey?: string;
  /** Keep the file browser expanded on load (advanced lessons). */
  readonly filesPanelExpanded?: boolean;
  /** Lesson assignment: enables Check My Work with browser-run checks. */
  readonly lessonAssignment?: {
    readonly lessonTestsForMarkDone: readonly LessonRuntimeMarkDoneSpec[];
    readonly markDoneSubmissionTarget?: { cohortId: string; lessonId: string };
    readonly assignmentProgressStatus?: AssignmentProgressStatus;
  };
  /** When set, enables Start over (reset to starter files) for open lesson assignments. */
  readonly workspaceResetTarget?: { cohortId: string; lessonId: string };
  /** Teacher review mode: no persistence or file operations; Run still works in-memory. */
  readonly readOnly?: boolean;
  /** Preview mode: allow editing but never read or write localStorage for this workspace. */
  readonly disableLocalPersist?: boolean;
  readonly subtitleNote?: string;
  /**
   * Quick script mode: an ephemeral scratch workspace with a Simple/Advanced
   * (file browser) toggle, a "Save as playground" action, and a mobile tab bar
   * that shows one panel at a time. Use with `filesStorageKey` (no `projectId`).
   */
  readonly quickScript?: {
    readonly defaultMode?: QuickScriptMode;
  };
  readonly tourSteps?: readonly DemoTourStep[];
  /** When set, themes the page background with the lesson's colors + icon watermark and plays an intro. */
  readonly immersiveTheme?: LessonImmersiveTheme;
  /** "wash" plays the full takeover intro; "arrive" (launched via the cross-fade overlay) only fades content in. */
  readonly introMode?: "wash" | "arrive";
}

function loadStoredProjectFiles(
  storageKey: string,
  fallback: readonly PlaygroundProjectFile[]
): PlaygroundProjectFile[] {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return normalizePlaygroundProjectFiles([...fallback]);
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return normalizePlaygroundProjectFiles([...fallback]);
    }
    const mapped: PlaygroundProjectFile[] = [];
    for (const item of parsed) {
      if (!item || typeof item !== "object") continue;
      const o = item as Record<string, unknown>;
      if (typeof o.path === "string" && typeof o.content === "string") {
        mapped.push({ path: o.path, content: o.content });
      }
    }
    return normalizePlaygroundProjectFiles(mapped.length > 0 ? mapped : [...fallback]);
  } catch {
    return normalizePlaygroundProjectFiles([...fallback]);
  }
}

function commitFiles(
  prev: PlaygroundProjectFile[],
  transform: (files: PlaygroundProjectFile[]) => PlaygroundProjectFile[]
): PlaygroundProjectFile[] {
  return normalizePlaygroundProjectFiles(transform(prev));
}

type MarkDoneModal =
  | null
  | { kind: "result"; result: DemoMarkDoneResponse; submissionSaveError?: string }
  | { kind: "error"; message: string };

export function PlaygroundBody({
  eyebrow,
  title,
  backTo,
  backLabel,
  initialFiles,
  projectId,
  filesStorageKey,
  filesPanelExpanded = false,
  lessonAssignment,
  workspaceResetTarget,
  readOnly = false,
  disableLocalPersist = false,
  subtitleNote,
  quickScript,
  tourSteps,
  immersiveTheme,
  introMode = "wash",
}: PlaygroundBodyProps) {
  const { getBearerToken } = useAuth();
  const navigate = useNavigate();
  const quick = Boolean(quickScript);
  const [quickMode, setQuickMode] = useState<QuickScriptMode>(quickScript?.defaultMode ?? "simple");
  const [mobileTab, setMobileTab] = useState<QuickMobileTab>("code");
  const filesHidden = quick && quickMode === "simple";
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [saveAsTitle, setSaveAsTitle] = useState("");
  const [saveAsBusy, setSaveAsBusy] = useState(false);
  const [saveAsError, setSaveAsError] = useState<string | null>(null);
  const arrive = Boolean(immersiveTheme) && introMode === "arrive";
  const [washing, setWashing] = useState(Boolean(immersiveTheme) && introMode === "wash");
  const initialNormalized = normalizePlaygroundProjectFiles(
    initialFiles.length > 0 ? [...initialFiles] : [{ path: "main.py", content: "" }]
  );
  const persistLocally = !readOnly && !disableLocalPersist;
  const recoveredShadowRef = useRef(false);
  const [files, setFiles] = useState<PlaygroundProjectFile[]>(() => {
    if (filesStorageKey && persistLocally && !projectId) {
      return loadStoredProjectFiles(filesStorageKey, initialNormalized);
    }
    if (projectId && persistLocally) {
      const picked = filesPreferringShadow(projectId, initialNormalized);
      recoveredShadowRef.current = picked.recovered;
      return picked.files;
    }
    return initialNormalized;
  });
  const filesRef = useRef(files);
  filesRef.current = files;

  const [activePath, setActivePath] = useState<string>(() => {
    const normalized =
      filesStorageKey && persistLocally && !projectId
        ? loadStoredProjectFiles(filesStorageKey, initialNormalized)
        : projectId && persistLocally
          ? filesPreferringShadow(projectId, initialNormalized).files
          : initialNormalized;
    return normalized.some((f) => f.path === "main.py") ? "main.py" : normalized[0]?.path ?? "main.py";
  });
  const [targetFolder, setTargetFolder] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>(recoveredShadowRef.current ? "unsaved" : "saved");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fileOpError, setFileOpError] = useState<string | null>(null);
  const [syncNotice, setSyncNotice] = useState<string | null>(null);
  const [pendingNew, setPendingNew] = useState<PendingNewItem | null>(null);
  const [filesCollapsed, setFilesCollapsed] = useState<boolean>(() => {
    if (filesPanelExpanded) return false;
    const normalized = normalizePlaygroundProjectFiles(
      initialFiles.length > 0 ? [...initialFiles] : [{ path: "main.py", content: "" }]
    );
    return visiblePlaygroundFiles(normalized).length <= 1;
  });
  const [inputDraft, setInputDraft] = useState("");
  const [clearConsoleOnRun, setClearConsoleOnRun] = useState(true);
  const [markDoneBusy, setMarkDoneBusy] = useState(false);
  const [markDoneModal, setMarkDoneModal] = useState<MarkDoneModal>(null);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [assignmentProgress, setAssignmentProgress] = useState<AssignmentProgressStatus | null>(
    lessonAssignment?.assignmentProgressStatus ?? null
  );

  const editorHostRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<EditorView | null>(null);
  const panelsRef = useRef<HTMLDivElement | null>(null);
  const consoleInputRef = useRef<HTMLInputElement | null>(null);

  const panelSizes = usePlaygroundPanelSizes(panelsRef, {
    storageKey: filesStorageKey,
    filesCollapsed,
    onResize: () => editorRef.current?.requestMeasure(),
  });

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const saveSeqRef = useRef(0);
  const persistInFlightRef = useRef<Promise<void> | null>(null);
  /** Active file content as of the latest Run, used to keep edits typed during a run. */
  const runStartActiveDocRef = useRef<string | null>(null);
  const activePathRef = useRef(activePath);
  activePathRef.current = activePath;

  const persistFiles = useCallback(
    (next: PlaygroundProjectFile[]): Promise<void> => {
      if (readOnly || disableLocalPersist) return Promise.resolve();
      if (filesStorageKey && !projectId) {
        const sizeError = validatePlaygroundFileSet(next);
        if (sizeError) {
          setSaveStatus("error");
          setSaveError(sizeError);
          return Promise.resolve();
        }
        try {
          localStorage.setItem(filesStorageKey, JSON.stringify(next));
          setSaveStatus("saved");
          setSaveError(null);
        } catch {
          setSaveStatus("error");
          setSaveError("Could not save your work locally.");
        }
        return Promise.resolve();
      }
      if (!projectId) return Promise.resolve();
      writePlaygroundShadow(projectId, next);
      const bearer = getBearerToken();
      if (!bearer) {
        setSaveStatus("error");
        setSaveError(SIGN_IN_TO_SAVE_MESSAGE);
        return Promise.resolve();
      }
      const sizeError = validatePlaygroundFileSet(next);
      if (sizeError) {
        setSaveStatus("error");
        setSaveError(sizeError);
        return Promise.resolve();
      }
      const seq = ++saveSeqRef.current;
      setSaveStatus("saving");
      setSaveError(null);
      const request = putPlaygroundProject(bearer, projectId, { files: next })
        .then(() => {
          if (saveSeqRef.current !== seq) return;
          const shadow = readPlaygroundShadow(projectId);
          if (!shadow || playgroundFilesEqual(shadow, next)) {
            clearPlaygroundShadow(projectId);
            setSaveStatus("saved");
          }
        })
        .catch((e: unknown) => {
          if (saveSeqRef.current !== seq) return;
          setSaveStatus("error");
          setSaveError(userFacingRequestError(e, "Could not save the project.", SIGN_IN_TO_SAVE_MESSAGE));
        })
        .finally(() => {
          if (persistInFlightRef.current === request) persistInFlightRef.current = null;
        });
      persistInFlightRef.current = request;
      return request;
    },
    [disableLocalPersist, filesStorageKey, getBearerToken, projectId, readOnly]
  );

  const schedulePersist = useCallback(() => {
    if (readOnly || disableLocalPersist) return;
    setSaveStatus("unsaved");
    if (projectId) writePlaygroundShadow(projectId, filesRef.current);
    if (!projectId && !filesStorageKey) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = undefined;
      persistFiles(filesRef.current);
    }, AUTOSAVE_DEBOUNCE_MS);
  }, [disableLocalPersist, filesStorageKey, persistFiles, projectId, readOnly]);

  const flushPersist = useCallback((): Promise<void> => {
    if (readOnly || disableLocalPersist) return Promise.resolve();
    if (!projectId && !filesStorageKey) return Promise.resolve();
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = undefined;
    }
    return persistFiles(filesRef.current);
  }, [disableLocalPersist, filesStorageKey, persistFiles, projectId, readOnly]);

  const updateFileContent = useCallback((path: string, content: string) => {
    const next = filesRef.current.map((f) => (f.path === path ? { ...f, content } : f));
    filesRef.current = next;
    setFiles(next);
  }, []);

  const onProjectFilesSync = useCallback(
    (synced: PlaygroundProjectFile[], skippedPaths: string[]) => {
      const view = editorRef.current;
      const active = activePathRef.current;
      const editorDoc = view?.state.doc.toString() ?? null;
      const runStartDoc = runStartActiveDocRef.current;
      runStartActiveDocRef.current = null;

      let next = normalizePlaygroundProjectFiles(synced);
      const editedDuringRun = editorDoc !== null && runStartDoc !== null && editorDoc !== runStartDoc;
      if (editedDuringRun && active && !isHiddenPlaygroundFile(active)) {
        const idx = next.findIndex((f) => f.path === active);
        if (idx >= 0) next = next.map((f, i) => (i === idx ? { ...f, content: editorDoc! } : f));
        else next = normalizePlaygroundProjectFiles([...next, { path: active, content: editorDoc! }]);
      }

      setFiles(next);
      const activeAfter = next.find((f) => f.path === active);
      if (!activeAfter || isHiddenPlaygroundFile(active)) {
        const visible = visiblePlaygroundFiles(next);
        setActivePath(visible.some((f) => f.path === "main.py") ? "main.py" : visible[0]?.path ?? "main.py");
      } else if (view && !editedDuringRun && editorDoc !== activeAfter.content) {
        view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: activeAfter.content } });
      }

      const visibleSkipped = skippedPaths.filter((p) => !isHiddenPlaygroundFile(p));
      setSyncNotice(
        visibleSkipped.length > 0
          ? `Some files written by the program were not kept (not text or too large): ${visibleSkipped.join(", ")}`
          : null
      );

      if (!readOnly && (projectId || filesStorageKey)) {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
          debounceRef.current = undefined;
        }
        persistFiles(next);
      }
    },
    [filesStorageKey, persistFiles, projectId, readOnly]
  );

  const {
    phase,
    runtimeError,
    lines,
    inputPrompt,
    canUseSharedMemory,
    turtleHtml,
    turtleTabVisible,
    outputTab,
    setOutputTab,
    runProject,
    stop,
    submitInput,
    clearConsole,
    runLessonTests,
  } = usePythonRuntime({
    onProjectFilesSync,
    deferTurtleDisplay: async () => {
      if (persistInFlightRef.current) await persistInFlightRef.current;
    },
  });

  // Recreate the editor whenever the active file changes.
  useEffect(() => {
    const host = editorHostRef.current;
    if (!host) return;
    if (isHiddenPlaygroundFile(activePath)) return;
    const file = filesRef.current.find((f) => f.path === activePath);
    const isPython = activePath.endsWith(".py");

    const view = new EditorView({
      state: EditorState.create({
        doc: file?.content ?? "",
        extensions: [
          basicSetup,
          ...(isPython ? [python(), ...pythonEditorExtensions] : []),
          ideEditorTheme,
          ...(readOnly ? [EditorView.editable.of(false), EditorState.readOnly.of(true)] : []),
          EditorView.lineWrapping,
          EditorView.updateListener.of((update) => {
            if (!update.docChanged || readOnly) return;
            updateFileContent(activePath, update.state.doc.toString());
            schedulePersist();
          }),
        ],
      }),
      parent: host,
    });
    editorRef.current = view;
    return () => {
      view.destroy();
      editorRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePath, readOnly]);

  const persistFilesRef = useRef(persistFiles);
  persistFilesRef.current = persistFiles;

  useEffect(() => {
    if (!recoveredShadowRef.current) return;
    recoveredShadowRef.current = false;
    setSyncNotice("Restored edits that had not reached your account yet.");
    void persistFilesRef.current(filesRef.current);
  }, []);

  useEffect(() => {
    const flush = () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = undefined;
      }
      void persistFilesRef.current(filesRef.current);
    };
    const onHide = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", flush);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, []);

  useEffect(() => {
    if (phase === "awaiting_input") consoleInputRef.current?.focus();
  }, [phase]);

  // Simple quick-script mode shows only main.py; keep the active file and mobile tab valid.
  useEffect(() => {
    if (!filesHidden) return;
    setActivePath((cur) => (cur === "main.py" ? cur : "main.py"));
    setMobileTab((cur) => (cur === "files" ? "code" : cur));
  }, [filesHidden]);

  const openSaveAs = useCallback(() => {
    setSaveAsError(null);
    setSaveAsTitle("");
    setSaveAsOpen(true);
  }, []);

  const onSaveAsPlayground = useCallback(async () => {
    const bearer = getBearerToken();
    const title = saveAsTitle.trim();
    if (!title) {
      setSaveAsError("Give your playground a name.");
      return;
    }
    if (!bearer) {
      setSaveAsError("Sign in again to save your work.");
      return;
    }
    setSaveAsBusy(true);
    setSaveAsError(null);
    try {
      const snapshot = normalizePlaygroundProjectFiles(filesRef.current);
      const data = await postPlaygroundProject(bearer, { title, files: snapshot });
      navigate(`/playground/${data.project.id}`);
    } catch (e) {
      setSaveAsError(e instanceof Error ? e.message : "Could not save this as a playground.");
      setSaveAsBusy(false);
    }
  }, [getBearerToken, navigate, saveAsTitle]);

  const onRun = useCallback(() => {
    setFileOpError(null);
    setSyncNotice(null);
    if (clearConsoleOnRun) clearConsole();
    const snapshot = normalizePlaygroundProjectFiles(filesRef.current);
    filesRef.current = snapshot;
    setFiles(snapshot);
    const active = snapshot.find((f) => f.path === activePathRef.current);
    runStartActiveDocRef.current = active?.content ?? null;
    void flushPersist().then(() => {
      runProject(snapshot, "main.py");
    });
  }, [clearConsole, clearConsoleOnRun, flushPersist, runProject]);

  const onSubmitConsoleInput = useCallback(() => {
    submitInput(inputDraft);
    setInputDraft("");
  }, [inputDraft, submitInput]);

  const openFile = useCallback((path: string) => {
    setActivePath(path);
  }, []);

  const startNewFile = useCallback(() => {
    setFileOpError(null);
    setFilesCollapsed(false);
    setPendingNew({ kind: "file", parent: targetFolder });
  }, [targetFolder]);

  const startNewFolder = useCallback(() => {
    setFileOpError(null);
    setFilesCollapsed(false);
    setPendingNew({ kind: "folder", parent: targetFolder });
  }, [targetFolder]);

  const cancelNew = useCallback(() => setPendingNew(null), []);

  const commitNew = useCallback(
    (rawName: string) => {
      const pending = pendingNew;
      if (!pending) return;
      const name = rawName.trim();
      if (!name) {
        setPendingNew(null);
        return;
      }
      if (pending.kind === "file") {
        const path = inferPlaygroundFilePath(name, pending.parent);
        const pathError = validatePlaygroundFilePath(path);
        if (pathError) {
          setFileOpError(pathError);
          return;
        }
        if (filesRef.current.some((f) => f.path === path)) {
          setFileOpError(`A file named ${path} already exists.`);
          return;
        }
        setFileOpError(null);
        setPendingNew(null);
        setFiles((prev) => commitFiles(prev, (cur) => [...cur, { path, content: "" }]));
        setActivePath(path);
        schedulePersist();
      } else {
        const folderPath = joinPlaygroundPath(pending.parent, name);
        const pathError = validatePlaygroundFolderPath(folderPath);
        if (pathError) {
          setFileOpError(pathError);
          return;
        }
        const folders = collectFolderPaths(filesRef.current);
        if (folders.has(folderPath)) {
          setFileOpError(`Folder ${folderPath}/ already exists.`);
          return;
        }
        setFileOpError(null);
        setPendingNew(null);
        setFiles((prev) =>
          commitFiles(prev, (cur) => [...cur, { path: `${folderPath}/__init__.py`, content: "" }])
        );
        setTargetFolder(folderPath);
        schedulePersist();
      }
    },
    [pendingNew, schedulePersist]
  );

  const onMoveFile = useCallback(
    (path: string, destFolder: string | null) => {
      if (path === "main.py") return;
      const base = path.split("/").pop()!;
      const dest = joinPlaygroundPath(destFolder, base);
      if (dest === path) return;
      const pathError = validatePlaygroundFilePath(dest);
      if (pathError) {
        setFileOpError(pathError);
        return;
      }
      if (filesRef.current.some((f) => f.path === dest)) {
        setFileOpError(`A file named ${dest} already exists.`);
        return;
      }
      setFileOpError(null);
      setFiles((prev) => commitFiles(prev, (cur) => cur.map((f) => (f.path === path ? { ...f, path: dest } : f))));
      setActivePath((cur) => (cur === path ? dest : cur));
      schedulePersist();
    },
    [schedulePersist]
  );

  const onMoveFolder = useCallback(
    (folderPath: string, destFolder: string | null) => {
      // Can't drop a folder into itself or one of its descendants.
      if (destFolder !== null && (destFolder === folderPath || destFolder.startsWith(`${folderPath}/`))) return;
      const base = folderPath.split("/").pop()!;
      const dest = joinPlaygroundPath(destFolder, base);
      if (dest === folderPath) return;
      const folders = collectFolderPaths(filesRef.current);
      if (folders.has(dest)) {
        setFileOpError(`Folder ${dest}/ already exists.`);
        return;
      }
      setFileOpError(null);
      setFiles((prev) => commitFiles(prev, (cur) => renamePlaygroundFolderPaths(cur, folderPath, dest)));
      setTargetFolder((cur) =>
        cur === folderPath ? dest : cur?.startsWith(`${folderPath}/`) ? `${dest}${cur.slice(folderPath.length)}` : cur
      );
      setActivePath((cur) => (cur.startsWith(`${folderPath}/`) ? `${dest}${cur.slice(folderPath.length)}` : cur));
      schedulePersist();
    },
    [schedulePersist]
  );

  const onRenameFile = useCallback(
    (path: string) => {
      if (path === "main.py") return;
      const next = window.prompt("Rename file", path)?.trim();
      if (!next || next === path) return;
      const resolved = inferPlaygroundFilePath(next, null);
      const pathError = validatePlaygroundFilePath(resolved);
      if (pathError) {
        setFileOpError(pathError);
        return;
      }
      if (resolved === "main.py" || filesRef.current.some((f) => f.path === resolved)) {
        setFileOpError(`A file named ${resolved} already exists.`);
        return;
      }
      setFileOpError(null);
      setFiles((prev) => commitFiles(prev, (cur) => cur.map((f) => (f.path === path ? { ...f, path: resolved } : f))));
      setActivePath((cur) => (cur === path ? resolved : cur));
      schedulePersist();
    },
    [schedulePersist]
  );

  const onDeleteFile = useCallback(
    (path: string) => {
      if (path === "main.py") return;
      if (!window.confirm(`Delete ${path}? This cannot be undone.`)) return;
      setFileOpError(null);
      setFiles((prev) => commitFiles(prev, (cur) => cur.filter((f) => f.path !== path)));
      setActivePath((cur) => (cur === path ? "main.py" : cur));
      schedulePersist();
    },
    [schedulePersist]
  );

  const onRenameFolder = useCallback(
    (folderPath: string) => {
      const next = window.prompt("Rename folder", folderPath)?.trim();
      if (!next || next === folderPath) return;
      const resolved = next.includes("/")
        ? next.replace(/\/+$/, "")
        : joinPlaygroundPath(parentFolderPath(folderPath), next);
      const pathError = validatePlaygroundFolderPath(resolved);
      if (pathError) {
        setFileOpError(pathError);
        return;
      }
      const folders = collectFolderPaths(filesRef.current);
      if (folders.has(resolved)) {
        setFileOpError(`Folder ${resolved}/ already exists.`);
        return;
      }
      setFileOpError(null);
      setFiles((prev) => commitFiles(prev, (cur) => renamePlaygroundFolderPaths(cur, folderPath, resolved)));
      setTargetFolder((cur) => (cur === folderPath ? resolved : cur?.startsWith(`${folderPath}/`) ? `${resolved}${cur.slice(folderPath.length)}` : cur));
      setActivePath((cur) => (cur.startsWith(`${folderPath}/`) ? `${resolved}${cur.slice(folderPath.length)}` : cur));
      schedulePersist();
    },
    [schedulePersist]
  );

  const onDeleteFolder = useCallback(
    (folderPath: string) => {
      if (!window.confirm(`Delete folder ${folderPath}/ and everything inside it? This cannot be undone.`)) return;
      setFileOpError(null);
      setFiles((prev) => commitFiles(prev, (cur) => deletePlaygroundFolderPaths(cur, folderPath)));
      setTargetFolder((cur) => (cur === folderPath || cur?.startsWith(`${folderPath}/`) ? null : cur));
      setActivePath((cur) => (cur.startsWith(`${folderPath}/`) ? "main.py" : cur));
      schedulePersist();
    },
    [schedulePersist]
  );

  const onUploadFiles = useCallback(
    async (list: FileList | null) => {
      if (!list || list.length === 0) return;
      setFileOpError(null);
      const rejected: string[] = [];
      const added: PlaygroundProjectFile[] = [];
      for (const file of Array.from(list)) {
        const name = inferPlaygroundFilePath(file.name.trim(), targetFolder);
        if (validatePlaygroundFilePath(name)) {
          rejected.push(`${file.name} (invalid name)`);
          continue;
        }
        const text = await readUploadAsText(file);
        if (text === null) {
          rejected.push(`${file.name} (not a text file)`);
          continue;
        }
        added.push({ path: name, content: text });
      }
      if (added.length > 0) {
        setFiles((prev) =>
          commitFiles(prev, (cur) => {
            const byPath = new Map(cur.map((f) => [f.path, f]));
            for (const f of added) byPath.set(f.path, f);
            return [...byPath.values()];
          })
        );
        const view = editorRef.current;
        const lastAdded = added[added.length - 1]!;
        setActivePath(lastAdded.path);
        const activeUpload = added.find((f) => f.path === lastAdded.path);
        if (view && activeUpload) {
          view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: activeUpload.content } });
        }
        schedulePersist();
      }
      if (rejected.length > 0) {
        setFileOpError(`Skipped: ${rejected.join(", ")}. Only UTF-8 text files are supported.`);
      }
    },
    [schedulePersist, targetFolder]
  );

  const running = phase === "running" || phase === "awaiting_input";
  const loadingRuntime = phase === "loading" && canUseSharedMemory;
  const lessonTests = lessonAssignment?.lessonTestsForMarkDone;
  const markDoneSubmissionTarget = lessonAssignment?.markDoneSubmissionTarget;

  useEffect(() => {
    if (lessonAssignment?.assignmentProgressStatus) {
      setAssignmentProgress(lessonAssignment.assignmentProgressStatus);
    }
  }, [lessonAssignment?.assignmentProgressStatus]);

  const onMarkDone = useCallback(async () => {
    const token = getBearerToken();
    if (lessonTests === undefined) return;
    if (!token) {
      setMarkDoneModal({ kind: "error", message: SIGN_IN_TO_CHECK_MESSAGE });
      return;
    }
    const snapshot = normalizePlaygroundProjectFiles(filesRef.current);
    filesRef.current = snapshot;
    setFiles(snapshot);
    const src = mainPyFromProjectFiles(snapshot);
    await flushPersist();
    setMarkDoneBusy(true);
    setMarkDoneModal(null);
    try {
      if (lessonTests.length === 0) {
        celebratePassing();
        setMarkDoneModal({
          kind: "result",
          result: {
            submissionId: `local-${crypto.randomUUID()}`,
            outcome: "passed",
            summary: "This lesson has no automated checks configured.",
            checks: [{ id: "no-lesson-tests", label: "No automated checks on this lesson", passed: true }],
            validationEngine: "lesson_autocheck_browser",
          },
        });
        return;
      }
      const blocks = await runLessonTests(
        src,
        lessonTests.map((t) => ({ id: t.id, name: t.name, testCode: t.testCode }))
      );
      const result = buildMarkDoneFromLessonTestBlocks(blocks, lessonTests);
      const flags = submissionFlagsFromBlocks(blocks, lessonTests);
      let submissionSaveError: string | undefined;
      if (markDoneSubmissionTarget) {
        try {
          await postStudentLessonSubmission(
            token,
            markDoneSubmissionTarget.cohortId,
            markDoneSubmissionTarget.lessonId,
            {
              workspaceFiles: snapshot,
              validationEngine: "browser_pyodide_lesson_tests_v1",
              overallStatus: result.outcome === "passed" ? "passed" : "failed",
              requiredPassed: flags.requiredPassed,
              optionalPassed: flags.optionalPassed,
              testResults: buildServerTestResults(blocks, lessonTests),
              submissionKind: "normal",
            }
          );
          setAssignmentProgress((prev) => {
            if (flags.requiredPassed) return "complete";
            if (prev === "complete") return "complete";
            return "needs_help";
          });
        } catch (e) {
          submissionSaveError = userFacingRequestError(
            e,
            "Could not save your attempt to the server (try again later).",
            SIGN_IN_TO_CHECK_MESSAGE
          );
        }
      }
      if (result.outcome === "passed" && flags.requiredPassed) {
        celebratePassing();
      }
      setMarkDoneModal({ kind: "result", result, submissionSaveError });
    } catch (e) {
      setMarkDoneModal({
        kind: "error",
        message: userFacingRequestError(e, "Check My Work failed", SIGN_IN_TO_CHECK_MESSAGE),
      });
    } finally {
      setMarkDoneBusy(false);
    }
  }, [flushPersist, getBearerToken, lessonTests, markDoneSubmissionTarget, runLessonTests]);

  const workspaceNote =
    subtitleNote ??
    (disableLocalPersist
      ? "Preview mode. Run and edit freely; nothing is saved."
      : readOnly
        ? "Read-only view of this project. You can run it to see its output; nothing is saved."
        : quick
          ? "Scratch space. Your code stays in this browser. Tap Save as playground to keep it on your account."
          : "Files save to your account automatically, including files your program writes when a run finishes.");

  const onConfirmReset = async () => {
    if (!workspaceResetTarget) return;
    const token = getBearerToken();
    if (!token) {
      setResetConfirmOpen(false);
      setMarkDoneModal({ kind: "error", message: SIGN_IN_TO_SAVE_MESSAGE });
      return;
    }
    setResetBusy(true);
    try {
      const result = await postStudentLessonWorkspaceReset(
        token,
        workspaceResetTarget.cohortId,
        workspaceResetTarget.lessonId
      );
      const starter = starterFilesToProjectFiles(result.starterFiles);
      if (filesStorageKey) {
        try {
          localStorage.removeItem(filesStorageKey);
        } catch {
          /* ignore */
        }
      }
      filesRef.current = starter;
      setFiles(starter);
      if (projectId) clearPlaygroundShadow(projectId);
      setActivePath(starter.some((f) => f.path === "main.py") ? "main.py" : starter[0]?.path ?? "main.py");
      setSaveStatus("saved");
      setResetConfirmOpen(false);
    } catch (e) {
      setMarkDoneModal({
        kind: "error",
        message: e instanceof Error ? e.message : "Could not reset project",
      });
      setResetConfirmOpen(false);
    } finally {
      setResetBusy(false);
    }
  };

  return (
    <div
      className={[
        "ide-page",
        "ide-page--full",
        immersiveTheme ? "ide-page--themed" : "",
        washing ? "ide-page--intro" : "",
        arrive ? "ide-page--arrive" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={immersiveTheme?.style}
      onAnimationEnd={
        washing
          ? (e) => {
              if (e.target === e.currentTarget) setWashing(false);
            }
          : undefined
      }
    >
      {immersiveTheme ? (
        <div className="ide-immersive-bg" aria-hidden="true">
          {immersiveTheme.iconDef ? (
            <FontAwesomeIcon icon={immersiveTheme.iconDef} className="ide-immersive-watermark" />
          ) : null}
        </div>
      ) : null}
      <header className="ide-header">
        <div className="ide-header-left">
          <Link to={backTo} className="ide-back">
            ← {backLabel}
          </Link>
          <div className="ide-title-block">
            <p className="ide-eyebrow">{eyebrow}</p>
            <h1 className="ide-title">{title}</h1>
          </div>
          {assignmentProgress ? (
            <div className={`ide-assignment-progress ide-assignment-progress--${assignmentProgress}`}>
              <ProgressStatusChip status={assignmentProgress} />
              <span className="muted small">{LAB_PROGRESS_HINTS[assignmentProgress]}</span>
            </div>
          ) : null}
        </div>
        <div className="ide-header-actions">
          {persistLocally && (projectId || filesStorageKey) ? (
            <span className="ide-save-status-wrap" aria-live="polite">
              <span
                className={`ide-save-status ide-save-status--${saveStatus}`}
                title={
                  saveError ??
                  (filesStorageKey && !projectId
                    ? "Your work is saved in this browser."
                    : "Your project is saved to your account.")
                }
              >
                {saveStatus === "saved"
                  ? "Saved"
                  : saveStatus === "unsaved"
                    ? "Unsaved changes"
                    : saveStatus === "saving"
                      ? "Saving…"
                      : "Save failed"}
              </span>
              {saveStatus === "error" ? (
                <button
                  type="button"
                  className="ide-save-retry btn secondary ide-btn tight"
                  onClick={() => flushPersist()}
                >
                  Retry
                </button>
              ) : null}
            </span>
          ) : null}
          <span className={`ide-status ide-status--${phase}`} aria-live="polite">
            {loadingRuntime
              ? "Loading Python…"
              : phase === "ready"
                ? "Ready"
                : phase === "running"
                  ? "Running"
                  : phase === "awaiting_input"
                    ? "Waiting for input"
                    : "-"}
          </span>
          <button type="button" className="btn secondary ide-btn" onClick={() => clearConsole()} disabled={running}>
            Clear console
          </button>
          {!readOnly && workspaceResetTarget ? (
            <button
              type="button"
              className="btn secondary ide-btn"
              onClick={() => setResetConfirmOpen(true)}
              disabled={resetBusy || running}
            >
              Start over
            </button>
          ) : null}
          <button
            type="button"
            className="btn secondary ide-btn"
            onClick={() => stop()}
            disabled={!running || loadingRuntime}
          >
            Stop
          </button>
          <button
            type="button"
            className="btn primary ide-btn"
            onClick={() => onRun()}
            disabled={running || loadingRuntime || !canUseSharedMemory}
          >
            Run
          </button>
          {lessonTests !== undefined && !readOnly ? (
            <button
              type="button"
              className="btn primary ide-btn"
              disabled={markDoneBusy || running || loadingRuntime}
              onClick={() => void onMarkDone()}
            >
              <FontAwesomeIcon icon={faCircleCheck} />{" "}
              {markDoneBusy ? "Checking…" : "Check My Work"}
            </button>
          ) : null}
        </div>
      </header>

      <p className="ide-account muted small">{workspaceNote}</p>

      {runtimeError ? <div className="warn-banner ide-banner">{runtimeError}</div> : null}
      {saveStatus === "error" && saveError ? (
        <div className="warn-banner ide-banner" role="alert">
          {saveError}
        </div>
      ) : null}
      {fileOpError ? (
        <div className="warn-banner ide-banner" role="alert">
          {fileOpError}
        </div>
      ) : null}
      {syncNotice ? <div className="warn-banner ide-banner">{syncNotice}</div> : null}

      {quick ? (
        <div className="quick-toolbar">
          <div className="quick-mode-switch" role="group" aria-label="Workspace mode">
            <button
              type="button"
              className={`quick-mode-btn${quickMode === "simple" ? " is-active" : ""}`}
              aria-pressed={quickMode === "simple"}
              onClick={() => setQuickMode("simple")}
            >
              Simple
            </button>
            <button
              type="button"
              className={`quick-mode-btn${quickMode === "advanced" ? " is-active" : ""}`}
              aria-pressed={quickMode === "advanced"}
              onClick={() => setQuickMode("advanced")}
            >
              Advanced
            </button>
          </div>
          <span className="quick-mode-hint muted small">
            {quickMode === "simple" ? "Just code and a console." : "File browser for multi-file scripts."}
          </span>
          <button type="button" className="btn secondary ide-btn quick-save-btn" onClick={openSaveAs}>
            <FontAwesomeIcon icon={faFloppyDisk} /> Save as playground
          </button>
        </div>
      ) : null}

      {quick ? (
        <div className="quick-mobile-tabs" role="tablist" aria-label="Workspace views">
          <button
            type="button"
            role="tab"
            aria-selected={mobileTab === "code"}
            className={`quick-mobile-tab${mobileTab === "code" ? " is-active" : ""}`}
            onClick={() => setMobileTab("code")}
          >
            Code
          </button>
          {!filesHidden ? (
            <button
              type="button"
              role="tab"
              aria-selected={mobileTab === "files"}
              className={`quick-mobile-tab${mobileTab === "files" ? " is-active" : ""}`}
              onClick={() => setMobileTab("files")}
            >
              Files
            </button>
          ) : null}
          <button
            type="button"
            role="tab"
            aria-selected={mobileTab === "console"}
            className={`quick-mobile-tab${mobileTab === "console" ? " is-active" : ""}`}
            onClick={() => setMobileTab("console")}
          >
            Console
          </button>
        </div>
      ) : null}

      <div
        ref={panelsRef}
        className={[
          "ide-panels",
          quick ? "quick-panels" : "playground-panels",
          quick ? (filesHidden ? "quick-panels--simple" : "quick-panels--advanced") : "",
          !quick && filesCollapsed ? "playground-panels--files-collapsed" : "",
          !quick && panelSizes.wideLayout ? "playground-panels--resizable" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        data-mobile-tab={quick ? mobileTab : undefined}
        style={
          !quick && panelSizes.gridTemplateColumns
            ? { gridTemplateColumns: panelSizes.gridTemplateColumns }
            : undefined
        }
      >
        {filesHidden ? null : !quick && filesCollapsed ? (
          <section className="ide-editor-panel playground-files-panel playground-files-panel--collapsed" aria-label="Project files">
            <button
              type="button"
              className="playground-files-expand"
              onClick={() => setFilesCollapsed(false)}
              title="Show files"
              aria-label="Show files"
            >
              <FontAwesomeIcon icon={faAnglesRight} fixedWidth />
              <span className="playground-files-expand-label">Files</span>
            </button>
          </section>
        ) : (
          <section className="ide-editor-panel playground-files-panel" aria-label="Project files">
            <div className="ide-panel-label playground-files-head">
              <span>Files</span>
              <span className="playground-files-actions">
                {!readOnly ? (
                  <>
                    <button
                      type="button"
                      className="playground-files-action"
                      onClick={startNewFile}
                      title="New file"
                      aria-label="New file"
                    >
                      <FontAwesomeIcon icon={faFileCirclePlus} fixedWidth />
                    </button>
                    <button
                      type="button"
                      className="playground-files-action"
                      onClick={startNewFolder}
                      title="New folder"
                      aria-label="New folder"
                    >
                      <FontAwesomeIcon icon={faFolderPlus} fixedWidth />
                    </button>
                    <label className="playground-files-action playground-upload-label" title="Upload files">
                      <FontAwesomeIcon icon={faUpload} fixedWidth />
                      <input
                        type="file"
                        multiple
                        className="playground-upload-input"
                        aria-label="Upload files"
                        onChange={(e) => {
                          void onUploadFiles(e.target.files);
                          e.target.value = "";
                        }}
                      />
                    </label>
                  </>
                ) : null}
                {!quick ? (
                  <button
                    type="button"
                    className="playground-files-action"
                    onClick={() => setFilesCollapsed(true)}
                    title="Collapse file browser"
                    aria-label="Collapse file browser"
                  >
                    <FontAwesomeIcon icon={faAnglesLeft} fixedWidth />
                  </button>
                ) : null}
              </span>
            </div>
            <PlaygroundFileExplorer
              files={files}
              activePath={activePath}
              targetFolder={targetFolder}
              readOnly={readOnly}
              pendingNew={pendingNew}
              onOpenFile={openFile}
              onSelectFolder={setTargetFolder}
              onRenameFile={onRenameFile}
              onDeleteFile={onDeleteFile}
              onRenameFolder={onRenameFolder}
              onDeleteFolder={onDeleteFolder}
              onMoveFile={onMoveFile}
              onMoveFolder={onMoveFolder}
              onCommitNew={commitNew}
              onCancelNew={cancelNew}
            />
          </section>
        )}

        {!quick && panelSizes.showFilesSplitter ? (
          <PlaygroundPanelSplitter
            ariaLabel="Resize file browser"
            active={panelSizes.dragging === "files"}
            onPointerDown={panelSizes.onFilesSplitterPointerDown}
          />
        ) : null}

        <section className={`ide-editor-panel${quick ? " quick-editor-panel" : ""}`} aria-label="Editor">
          <div className="ide-panel-label">Editor: {activePath}</div>
          <div ref={editorHostRef} className="ide-editor-mount" />
        </section>

        {!quick && panelSizes.wideLayout ? (
          <PlaygroundPanelSplitter
            ariaLabel="Resize console"
            active={panelSizes.dragging === "console"}
            onPointerDown={panelSizes.onConsoleSplitterPointerDown}
          />
        ) : null}

        <IdeOutputPanel
          lines={lines}
          phase={phase}
          inputPrompt={inputPrompt}
          inputDraft={inputDraft}
          onInputDraftChange={setInputDraft}
          onSubmitInput={onSubmitConsoleInput}
          consoleInputRef={consoleInputRef}
          clearConsoleOnRun={clearConsoleOnRun}
          onClearConsoleOnRunChange={setClearConsoleOnRun}
          outputTab={outputTab}
          onOutputTabChange={setOutputTab}
          turtleHtml={turtleHtml}
          turtleTabVisible={turtleTabVisible}
        />
        {tourSteps && tourSteps.length > 0 ? <StudentIdeTourPanel steps={tourSteps} /> : null}
      </div>

      {markDoneModal ? (
        <div className="ide-modal-root" role="dialog" aria-modal="true" aria-labelledby="playground-mark-done-title">
          <button
            type="button"
            className="ide-modal-backdrop"
            aria-label="Close dialog"
            onClick={() => setMarkDoneModal(null)}
          />
          <div className="ide-modal-card card">
            {markDoneModal.kind === "error" ? (
              <>
                <h2 id="playground-mark-done-title" className="ide-modal-title">
                  Could not submit
                </h2>
                <p className="ide-modal-body">{markDoneModal.message}</p>
              </>
            ) : (
              <>
                <h2 id="playground-mark-done-title" className="ide-modal-title">
                  {markDoneModal.result.outcome === "passed" ? "🏆 Nice work!" : "🧩 Not quite yet"}
                </h2>
                <p
                  className={`ide-modal-outcome ide-modal-outcome--${markDoneModal.result.outcome}`}
                  aria-live="polite"
                >
                  {markDoneModal.result.summary}
                </p>
                <ul className="ide-modal-checks">
                  {markDoneModal.result.checks.map((c) => (
                    <li key={c.id} className={c.passed ? "ide-check-pass" : "ide-check-fail"}>
                      <span className="ide-check-mark" aria-hidden>
                        {c.passed ? "✓" : "✗"}
                      </span>
                      {c.label}
                    </li>
                  ))}
                </ul>
                {markDoneModal.submissionSaveError ? (
                  <p className="warn-banner ide-modal-body" role="alert">
                    {markDoneModal.submissionSaveError}
                  </p>
                ) : markDoneSubmissionTarget ? (
                  <p className="muted small ide-modal-engine">Your attempt was saved for this assignment.</p>
                ) : null}
              </>
            )}
            <div className="ide-modal-actions">
              <button type="button" className="btn primary" onClick={() => setMarkDoneModal(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {saveAsOpen ? (
        <div className="ide-modal-root" role="dialog" aria-modal="true" aria-labelledby="quick-save-title">
          <button
            type="button"
            className="ide-modal-backdrop"
            aria-label="Close dialog"
            onClick={() => (saveAsBusy ? undefined : setSaveAsOpen(false))}
          />
          <div className="ide-modal-card card">
            <h2 id="quick-save-title" className="ide-modal-title">
              Save as playground
            </h2>
            <p className="ide-modal-body muted small">
              Keep this script on your account as a playground you can reopen anytime.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void onSaveAsPlayground();
              }}
            >
              <label className="field">
                <span className="field-label">Playground name</span>
                <input
                  type="text"
                  className="input"
                  autoFocus
                  placeholder="e.g. My quick script"
                  value={saveAsTitle}
                  onChange={(e) => setSaveAsTitle(e.target.value)}
                  aria-label="Playground name"
                />
              </label>
              {saveAsError ? (
                <p className="warn-banner ide-modal-body" role="alert">
                  {saveAsError}
                </p>
              ) : null}
              <div className="ide-modal-actions">
                <button
                  type="button"
                  className="btn secondary"
                  onClick={() => setSaveAsOpen(false)}
                  disabled={saveAsBusy}
                >
                  Cancel
                </button>
                <button type="submit" className="btn primary" disabled={saveAsBusy || !saveAsTitle.trim()}>
                  {saveAsBusy ? "Saving…" : "Save playground"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {resetConfirmOpen ? (
        <div className="ide-modal-root" role="dialog" aria-modal="true" aria-labelledby="playground-reset-title">
          <button
            type="button"
            className="ide-modal-backdrop"
            aria-label="Close dialog"
            onClick={() => !resetBusy && setResetConfirmOpen(false)}
          />
          <div className="ide-modal-card card">
            <h2 id="playground-reset-title" className="ide-modal-title">
              Start over?
            </h2>
            <p className="ide-modal-body">
              Your changes will be undone. Your project will go back to the original starter code from your
              teacher.
            </p>
            <div className="ide-modal-actions">
              <button
                type="button"
                className="btn secondary"
                onClick={() => setResetConfirmOpen(false)}
                disabled={resetBusy}
              >
                Cancel
              </button>
              <button type="button" className="btn primary" onClick={() => void onConfirmReset()} disabled={resetBusy}>
                {resetBusy ? "Resetting…" : "Reset project"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
